import { SvpToken } from '../../../utils/token';

const createUrlParameterReplacer =
  (param: string, replacer: string) =>
  (input: string): string => {
    const url = new URL(input);
    if (url.searchParams.has(param)) {
      url.searchParams.set(param, replacer);
    }
    return url.toString();
  };

export class PlaylistItemManager {
  private readonly item: jwplayer.PlaylistItem;

  constructor(playlistItem: jwplayer.PlaylistItem) {
    this.item = playlistItem;
  }

  static fromPlaylist(player: jwplayer.JWPlayer) {
    const playlistItem = player.getPlaylistItem(player.getPlaylistIndex());

    return playlistItem ? new PlaylistItemManager(playlistItem) : undefined;
  }

  getToken() {
    return SvpToken.fromPlaylistItem(this.item);
  }

  hasValidToken() {
    const token = this.getToken();
    return token.isValid();
  }

  async refreshToken() {
    const token = this.getToken();
    if (token.isValid()) {
      return false;
    }

    const refreshed = await token.refresh(() =>
      this.item.svp.settings.adn.svpPlayer.config.getToken()
    );
    if (refreshed) {
      this.setToken(token);
    }
    return refreshed;
  }

  setToken(token: SvpToken) {
    const replaceUrlToken = createUrlParameterReplacer(
      'hdnea',
      token.toString()
    );
    [this.item, ...(this.item.allSources || [])].forEach((s) => {
      s.file = replaceUrlToken(s.file);
    });
  }

  removePrerollAds() {
    const { adschedule } = this.item;
    if (!adschedule) return;

    // VMAP: adschedule is a URL string - the entire schedule is server-controlled
    // and cannot be filtered client-side. Delete it so no ads replay on recovery.
    if (typeof adschedule === 'string') {
      delete this.item.adschedule;
      return;
    }

    Object.keys(adschedule).forEach((key) => {
      if (adschedule[key].offset === 'pre') {
        delete adschedule[key];
      }
    });
  }

  removePauseAd() {
    const { adPlacements } = this.item;
    if (!adPlacements) return;

    if ('pause' in adPlacements) {
      delete adPlacements.pause;
    }
  }

  removeMidrollAdsBeforeTime(time = 0) {
    const { adschedule } = this.item;
    if (!adschedule) {
      return;
    }

    // VMAP: adschedule is a URL string - individual midrolls cannot be filtered client-side.
    if (typeof adschedule === 'string') {
      return;
    }

    Object.keys(adschedule).forEach((key) => {
      const { offset } = adschedule[key];
      if (
        key.includes('midroll') &&
        offset !== 'pre' &&
        offset !== 'post' &&
        offset <= time
      ) {
        delete adschedule[key];
      }
    });
  }

  setStartTime(time: number) {
    this.item.starttime = time;
  }

  getItem() {
    return this.item;
  }
}
